import type { Router } from "vue-router";

const GUARD_KEY = "bitpeak:chunk-reload";

/** The messages Chrome, Safari and Firefox use when a dynamic import cannot be fetched. */
const CHUNK_LOAD_ERROR =
    /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i;

const readGuard = (storage: Storage): string | null => {
    try {
        return storage.getItem(GUARD_KEY);
    } catch {
        return null;
    }
};

const writeGuard = (storage: Storage, value: string | null): void => {
    try {
        if (value === null) {
            storage.removeItem(GUARD_KEY);
        } else {
            storage.setItem(GUARD_KEY, value);
        }
    } catch {
        // Storage can be unavailable (private mode); the reload then simply is not loop-guarded.
    }
};

/**
 * Every deploy replaces the hashed view chunks, so a tab opened before it fails
 * to load the next lazy route and the click silently does nothing. Load the
 * target URL once to pick up the new build; the guard stops a genuinely missing
 * chunk from reloading forever.
 */
export const installChunkReload = (router: Router, win: Window = window): void => {
    router.onError((error, to) => {
        if (!(error instanceof Error) || !CHUNK_LOAD_ERROR.test(error.message)) {
            return;
        }

        if (readGuard(win.sessionStorage) === to.fullPath) {
            return;
        }

        writeGuard(win.sessionStorage, to.fullPath);
        win.location.assign(to.fullPath);
    });

    router.afterEach((_to, _from, failure) => {
        if (!failure) {
            writeGuard(win.sessionStorage, null);
        }
    });

    win.addEventListener("vite:preloadError", (event) => {
        const marker = `preload:${win.location.href}`;

        if (readGuard(win.sessionStorage) === marker) {
            return;
        }

        event.preventDefault();
        writeGuard(win.sessionStorage, marker);
        win.location.reload();
    });
};
